import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';

import { getCurrentCompanyData } from 'domain/companies/companiesSelector';
import { documentsGridHeadersListSelector } from 'domain/documents';
import { OPERATORS } from 'components/AgGrid/helpers/filterHelpers';

// AG Grid filter model — the subset the label logic reads (ported from GridFilterTags).
interface AgGridFilterCondition {
  filterType?: string;
  type?: string;
  filter?: string | number;
  filterTo?: string | number | null;
  values?: Array<string | { label?: string }>;
  dateFrom?: string | null;
  dateTo?: string | null;
}
interface AgGridFilterModel extends AgGridFilterCondition {
  conditions?: AgGridFilterCondition[];
  operator?: string;
}

export interface GridFilterChip {
  readonly key: string; // colId
  readonly title: string; // column header name
  readonly text: string; // formatted filter value
}

const DEFAULT_FORMAT = 'MM/DD/YYYY';
const dateFormater = (value: string, dateFormat?: string) => moment(value).format(dateFormat || DEFAULT_FORMAT);

// Faithful port of GridFilterTags label derivation — labels only; removal is owned
// by useWorkspaceSearch (single dispatch source) and zipped in by the renderer.
export const useGridFilterChips = (gridFilters: Record<string, unknown>): GridFilterChip[] => {
  const intl = useIntl();
  const defaultColumns = useSelector(documentsGridHeadersListSelector) as Array<{ field: string; headerName: string }>;
  const currentCompanyData = useSelector(getCurrentCompanyData) as { dateFormat?: string } | null;

  const titles = useMemo(
    () =>
      defaultColumns
        ? defaultColumns.reduce(
            (res: Record<string, string>, header) => ({ ...res, [header.field]: header.headerName }),
            {},
          )
        : {},
    [defaultColumns],
  );

  const getTypeText = useCallback(
    (type: string) =>
      intl.formatMessage({ id: `company.workspace.grid.filters.operator.${type}`, defaultMessage: type }),
    [intl],
  );

  const textFilterHandler = useCallback(
    ({ type, filter }: AgGridFilterCondition) => (filter ? `${getTypeText(type!)}: ${filter}` : getTypeText(type!)),
    [getTypeText],
  );

  const numberFilterHandler = useCallback(
    ({ type, filter, filterTo = null }: AgGridFilterCondition) => {
      const value = filterTo === null ? filter : `${filter} - ${filterTo}`;
      return value !== null && value !== undefined ? `${getTypeText(type!)}: ${value}` : getTypeText(type!);
    },
    [getTypeText],
  );

  const setFilterHandler = useCallback(
    ({ type, values = [] }: AgGridFilterCondition) =>
      `${getTypeText(type!)}: ${values
        .map((value) => {
          const transKey = (typeof value === 'object' ? value.label : value) || 'blanks';
          return intl.formatMessage({
            id: `company.workspace.grid.filters.values.${transKey}`,
            defaultMessage: String(transKey),
          });
        })
        .join(', ')}`,
    [getTypeText, intl],
  );

  const dateFilterHandler = useCallback(
    ({ type, dateFrom = null, dateTo = null }: AgGridFilterCondition) => {
      const value =
        dateFrom && dateTo
          ? `${dateFrom} - ${dateTo}`
          : (dateFrom && dateFormater(dateFrom, currentCompanyData?.dateFormat)) ||
            (dateTo && dateFormater(dateTo, currentCompanyData?.dateFormat));
      return `${getTypeText(type!)}: ${value}`;
    },
    [getTypeText, currentCompanyData],
  );

  const filterTypeHandlers = useMemo(
    () => ({ set: setFilterHandler, text: textFilterHandler, number: numberFilterHandler, date: dateFilterHandler }),
    [setFilterHandler, textFilterHandler, numberFilterHandler, dateFilterHandler],
  );

  const getFilterItemText = useCallback(
    (filter: AgGridFilterCondition): string => {
      if (filter.type === OPERATORS.withInYear) return String(new Date(filter.dateFrom!).getFullYear());
      if (filter.type === OPERATORS.withInMonth) return moment(filter.dateFrom).format('MMM YYYY');
      const handler =
        filterTypeHandlers[filter.filterType as keyof typeof filterTypeHandlers] ||
        (() => {
          console.error(`Undefined filter type ${filter.filterType}`);
          return '';
        });
      return String(handler(filter));
    },
    [filterTypeHandlers],
  );

  const getFilterText = useCallback(
    (filters: AgGridFilterModel): string => {
      const { conditions = null, operator = null, ...currentFilter } = filters;
      return operator && conditions
        ? `${getFilterItemText(conditions[0])} ${getTypeText(operator.toLowerCase())} ${getFilterItemText(conditions[1])}`
        : getFilterItemText(currentFilter);
    },
    [getFilterItemText, getTypeText],
  );

  return useMemo(
    () =>
      Object.entries(gridFilters).map(([key, filter]) => ({
        key,
        title: titles[key] ?? key,
        text: getFilterText(filter as AgGridFilterModel),
      })),
    [gridFilters, titles, getFilterText],
  );
};
